import * as THREE from 'three';
import vertexShader from "../shaders/_text.vert";
import fragmentShader from "../shaders/_text.frag";
import { gsap } from 'gsap';

export class Text {
  private canvas = document.createElement('canvas');
  private ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
  private text = '';
  private particles: THREE.Points | null = null;
  private textPositions: number[] = [];
  private particleCounts = 20000;
  private FONT_ADJUST = 0.0025;

  private startButton = document.getElementById('start-button') as HTMLButtonElement | null;
  private isMobile = false;

  constructor(renderer: THREE.WebGLRenderer) {
    this.text = '100 knocks';
    this.isMobile = window.innerWidth < 768;
  }

  // ------------------------ Public ------------------------
  public getInstance() {
    return this.particles;
  }

  public update(elapsedTime: number): void {
  }

  public animation = () => {
    const progress = {
      t: 0.0
    };
    gsap.to(progress, {
      t: 2.0,
      duration: 7.0,
      ease: 'none',
      onUpdate: () => {
        const material = this.particles?.material as THREE.ShaderMaterial;
        if (material && material.uniforms && material.uniforms.uAnimationProgress) {
          material.uniforms.uAnimationProgress.value = progress.t;
        }
      }
    })
  }
  public async initialize(): Promise<void> {
    await this.createCanvasContent(this.text);
    this.textPositions = this.getTextPixels();

    this.particles = this.createParticles();
    if (this.startButton) {
      this.startButton.addEventListener('click', () => {
        this.animation();
      });
    }
  }



  // private ------------------------------------------------------------
  /**
   * テキストを表示するためのcanvasの作成
   * @param text
   */
  private createCanvasContent = async (text: string) => {
    this.canvas.width = 2048;
    this.canvas.height = 1024;

    this.ctx.fillStyle = '#000000';
    await document.fonts.ready.then(() => {
      this.ctx.font = '320px Helvetica';
      this.ctx.font = '320px Helvetica';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText(text, this.canvas.width / 2, this.canvas.height / 2);
    });
  }

  /**
   * テキストのピクセルデータを取得
   */
  private getTextPixels = () => {
    const imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    const pixels = imageData.data;

    const positions = [];
    const sampling = 2;

    for (let y = 0; y < this.canvas.height; y += sampling) {
      for (let x = 0; x < this.canvas.width; x += sampling) {
        const index = (y * this.canvas.width + x) * 4;
        const alpha = pixels[index + 3];

        if (alpha > 128) {
          positions.push((x - this.canvas.width / 2) * this.FONT_ADJUST, -(y - this.canvas.height / 2) * this.FONT_ADJUST, 0.0);
        };
      };
    };

    return positions;
  }

  private createParticles = () => {
    const BASE_POSITION = {
      x: 3.0,
      y: 3.0,
    };
    const MAX_POINTS = 14000;

    const totalTextPoints = Math.floor(this.textPositions.length / 3);
    const count = Math.min(MAX_POINTS, totalTextPoints);
    this.particleCounts = count;

    const vertices: number[] = [];
    const endPositions: number[] = [];
    const sampledTextPositions: number[] = [];
    const aIndex = new Float32Array(this.particleCounts);

    const step = totalTextPoints > 0 ? totalTextPoints / Math.max(1, this.particleCounts) : 1;
    for (let i = 0; i < this.particleCounts; i++) {
      aIndex[i] = i;
      const position = [BASE_POSITION.x * (Math.random() * 2 - 1), BASE_POSITION.y, 0.0];
      vertices.push(position[0], position[1], position[2]);

      const base = Math.min(Math.floor(i * step), Math.max(0, totalTextPoints - 1)) * 3;
      sampledTextPositions.push(
        this.textPositions[base + 0],
        this.textPositions[base + 1],
        this.textPositions[base + 2],
      );

      const endPosition = [BASE_POSITION.x * (Math.random() * 2 - 1), -BASE_POSITION.y, 0.0];
      endPositions.push(endPosition[0], endPosition[1], endPosition[2]);

    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('aStartPosition', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('aEndPosition', new THREE.Float32BufferAttribute(endPositions, 3));
    geometry.setAttribute('aTextPosition', new THREE.Float32BufferAttribute(sampledTextPositions, 3));
    geometry.setAttribute('aIndex', new THREE.Float32BufferAttribute(aIndex, 1));

    const innerWidth = window.innerWidth;
    const pointSize = 2.5 * (1440 / innerWidth);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0.0 },
        uPointSize: { value: this.isMobile ? 3.0 : pointSize },
        uAnimationProgress: { value: 0.0 },
      },
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      transparent: true,
      depthWrite: false,
    });

    const particles = new THREE.Points(geometry, material);
    return particles;
  }
}